import { Wrapper } from '@googlemaps/react-wrapper';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

export interface GoogleMapsMarkerOptionsWithId extends google.maps.MarkerOptions {
  id: string;
}

interface IGoogleMapsLocationProps extends google.maps.MapOptions {
  className?: string;
  markersOptions?: GoogleMapsMarkerOptionsWithId[];
  referenceMarkerOptions: GoogleMapsMarkerOptionsWithId | null;
  onMarkerClick: (id: string) => void;
}

const UnstyledGoogleMapsLocation: FC<React.PropsWithChildren<IGoogleMapsLocationProps>> = ({
  className,
  markersOptions = [],
  referenceMarkerOptions,
  onMarkerClick,
  ...mapOptions
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [referenceMarker, setReferenceMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);

  const setAllMarkers = () => {
    if (!map) return;

    setMarkers(
      markersOptions?.map((markerOptions) => {
        // Create icon element for AdvancedMarkerElement
        const iconImg = document.createElement('img');
        iconImg.src = '/images/map_markers/red-dot.png';
        iconImg.style.width = '30px';
        iconImg.style.height = '30px';

        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: markerOptions.position,
          content: iconImg,
          title: markerOptions.title,
        });

        marker.addListener('click', () => {
          onMarkerClick(markerOptions.id);
        });

        return marker;
      }) || []
    );
  };

  const clearAllMarkers = () => {
    markers.forEach((marker) => {
      marker.map = null;
    });
    setMarkers([]);
  };

  useEffect(() => {
    if (markersOptions.length > 0) {
      clearAllMarkers();
      setAllMarkers();
    }
  }, [markersOptions]);

  const createBounds = (boundMarkers: google.maps.marker.AdvancedMarkerElement[]) => {
    const bounds = new window.google.maps.LatLngBounds();

    boundMarkers.forEach((marker) => {
      if (marker.position) {
        bounds.extend(marker.position);
      }
    });

    return bounds;
  };

  useEffect(() => {
    if (!map) return;

    // handle single marker
    if (markers.length === 1) {
      if (referenceMarker) {
        const bounds = createBounds([referenceMarker, ...markers]);
        map.fitBounds(bounds);
        return;
      }

      map.setZoom(17);
      if (markers[0].position) {
        map.setCenter(markers[0].position);
      }
    }

    // handle multiple markers
    if (markers.length > 1) {
      const bounds = createBounds(markers);
      map.fitBounds(bounds);
      if (map.getZoom()! > 17) {
        map.setZoom(17);
      }
    }
  }, [markers, referenceMarker]);

  useEffect(() => {
    if (!map) return;

    if (referenceMarker) {
      referenceMarker.map = null;
    }

    if (!referenceMarkerOptions) {
      setReferenceMarker(null);
      return;
    }

    // Create icon element for reference marker
    const iconImg = document.createElement('img');
    iconImg.src = '/images/map_markers/white.png';
    iconImg.style.width = '30px';
    iconImg.style.height = '30px';

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: referenceMarkerOptions.position,
      content: iconImg,
      title: referenceMarkerOptions.title,
    });

    setReferenceMarker(marker);
  }, [referenceMarkerOptions]);

  const onRefChange = useCallback((node: any) => {
    if (node !== null) {
      setMap(
        new window.google.maps.Map(node, {
          zoom: 12,
          mapId: 'POINT_MAP',
          ...mapOptions,
        })
      );
    }
  }, []);

  return (
    <Wrapper apiKey={process.env.REACT_APP_GOOGLE_MAPS_APIKEY!} libraries={['marker']}>
      <div className={className} ref={onRefChange} />
    </Wrapper>
  );
};

export const GoogleMapsLocation = styled(UnstyledGoogleMapsLocation)`
  height: 100%;
  border-radius: 5px;
`;
